import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, CircleNotch, Trash, Heart, Trophy, Target, FloppyDisk, Lightning, Funnel, Warning, CheckCircle, XCircle, ArrowSquareOut } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { getAllConstraints } from '../components/MyPrediction';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SET_LABELS = ['C1', 'C2', 'C3'];
const SET_COLORS = [
  { bg: 'bg-blue-600', border: 'border-blue-400', gradient: 'from-blue-600 to-blue-800', light: 'bg-blue-50 border-blue-200', text: 'text-blue-700' },
  { bg: 'bg-purple-600', border: 'border-purple-400', gradient: 'from-purple-600 to-purple-800', light: 'bg-purple-50 border-purple-200', text: 'text-purple-700' },
  { bg: 'bg-teal-600', border: 'border-teal-400', gradient: 'from-teal-600 to-teal-800', light: 'bg-teal-50 border-teal-200', text: 'text-teal-700' },
];

function detectConflicts(constraints) {
  const conflicts = [];
  const c = constraints;
  if (!c) return conflicts;

  if (c.signPattern?.applied && c.directionPattern?.applied) {
    const signs = c.signPattern.value.split(',');
    const dirs = c.directionPattern.value.split(',');
    const mismatches = [];
    for (let i = 0; i < Math.min(signs.length, dirs.length, 5); i++) {
      const sign = signs[i]?.trim();
      const dir = dirs[i]?.trim();
      if ((sign === '+' && dir === 'DOWN') || (sign === '-' && dir === 'UP')) {
        mismatches.push(`B${i + 1}: Sign="${sign}" vs Dir="${dir}"`);
      }
    }
    if (mismatches.length > 0) {
      conflicts.push({ severity: 'high', windows: ['W3', 'W4'], title: 'Sign vs Direction Conflict', detail: mismatches.join('; '), suggestion: 'Signs and directions should agree per position.' });
    }
  }

  if (c.signPattern?.applied && c.comboPattern?.applied) {
    const signs = c.signPattern.value.split(',');
    const comboDirs = c.comboPattern.value.split(',');
    const mismatches = [];
    for (let i = 0; i < Math.min(signs.length, comboDirs.length, 5); i++) {
      const sign = signs[i]?.trim();
      const cd = comboDirs[i]?.trim();
      if ((sign === '+' && cd === 'DOWN') || (sign === '-' && cd === 'UP')) mismatches.push(`B${i + 1}`);
    }
    if (mismatches.length > 0) {
      conflicts.push({ severity: 'medium', windows: ['W3', 'W5'], title: 'Sign vs 7-Combo Conflict', detail: `${mismatches.length} pos conflict`, suggestion: 'W3 sign and W5 combo should agree.' });
    }
  }

  if (c.directionPattern?.applied && c.comboPattern?.applied) {
    const dirs = c.directionPattern.value.split(',');
    const comboDirs = c.comboPattern.value.split(',');
    const mismatches = [];
    for (let i = 0; i < Math.min(dirs.length, comboDirs.length, 5); i++) {
      const d = dirs[i]?.trim();
      const cd = comboDirs[i]?.trim();
      if ((d === 'UP' && cd === 'DOWN') || (d === 'DOWN' && cd === 'UP')) mismatches.push(`B${i + 1}`);
    }
    if (mismatches.length > 0) {
      conflicts.push({ severity: 'high', windows: ['W4', 'W5'], title: 'Direction vs 7-Combo Conflict', detail: `${mismatches.length} opposing directions`, suggestion: 'W4 and W5 should agree.' });
    }
  }

  if (c.tempPreference?.applied && c.momentumPreference?.applied) {
    const tp = JSON.parse(c.tempPreference.value || '{}');
    const mp = JSON.parse(c.momentumPreference.value || '{}');
    const posLabels = { ball_1: 'B1', ball_2: 'B2', ball_3: 'B3', ball_4: 'B4', ball_5: 'B5', lucky_star_1: 'LS1', lucky_star_2: 'LS2' };
    const mismatches = [];
    for (const pos of Object.keys(tp)) {
      if (mp[pos] && ((tp[pos] === 'HOT' && mp[pos] === 'COLD') || (tp[pos] === 'COLD' && mp[pos] === 'HOT'))) {
        mismatches.push(`${posLabels[pos]}: W11="${tp[pos]}" vs W21="${mp[pos]}"`);
      }
    }
    if (mismatches.length > 0) {
      conflicts.push({ severity: 'high', windows: ['W11', 'W21'], title: 'Temp vs Momentum Conflict', detail: mismatches.join('; '), suggestion: 'Opposing temp zones severely limit candidates.' });
    }
  }

  if (c.sumRange?.applied && c.rangePattern?.applied) {
    const sumMin = parseInt(c.sumRange.min);
    const sumMax = parseInt(c.sumRange.max);
    const parts = c.rangePattern.value.split('-').map(Number);
    if (parts.length === 5) {
      const minPerBucket = [1, 11, 21, 31, 41];
      const maxPerBucket = [10, 20, 30, 40, 50];
      let estMin = 0, estMax = 0;
      for (let b = 0; b < 5; b++) { estMin += parts[b] * minPerBucket[b]; estMax += parts[b] * maxPerBucket[b]; }
      if (estMax < sumMin || estMin > sumMax) {
        conflicts.push({ severity: 'high', windows: ['W14', 'W24'], title: 'Sum Range vs Range Pattern Conflict', detail: `Pattern implies ~${estMin}-${estMax}, sum is ${sumMin}-${sumMax}`, suggestion: 'Widen sum range or adjust range pattern.' });
      }
    }
  }

  if (c.signPattern?.applied && c.sumRange?.applied) {
    const signs = c.signPattern.value.split(',').map(s => s.trim());
    const sumMin = parseInt(c.sumRange.min);
    if (signs.every(s => s === '-') && sumMin > 100) {
      conflicts.push({ severity: 'medium', windows: ['W3', 'W14'], title: 'All-Negative + High Sum', detail: `All signs "-" but min sum ${sumMin}`, suggestion: 'Allow some "+" positions or lower min sum.' });
    }
  }

  return conflicts;
}

function getAppliedPayload(constraints) {
  const c = constraints;
  if (!c) return {};
  const payload = {};
  if (c.signPattern?.applied) payload.sign_pattern = c.signPattern.value;
  if (c.directionPattern?.applied) payload.direction_pattern = c.directionPattern.value;
  if (c.comboPattern?.applied) payload.combo_pattern = c.comboPattern.value;
  if (c.sumRange?.applied) { payload.sum_range_min = parseInt(c.sumRange.min); payload.sum_range_max = parseInt(c.sumRange.max); }
  if (c.rangePattern?.applied) payload.range_pattern = c.rangePattern.value;
  if (c.sharedPrediction?.applied) payload.shared_prediction = c.sharedPrediction.value;
  if (c.tempPreference?.applied) payload.temp_prefs = JSON.parse(c.tempPreference.value);
  if (c.momentumPreference?.applied) payload.momentum_prefs = JSON.parse(c.momentumPreference.value);
  if (c.monthlyFreqPreference?.applied) payload.number_picks = JSON.parse(c.monthlyFreqPreference.value);
  return payload;
}

export default function MyPicks() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [picks, setPicks] = useState([]);
  const [totalDraws, setTotalDraws] = useState(0);
  const [mainTab, setMainTab] = useState('generator');

  // 3 independent constraint sets
  const [sets, setSets] = useState([null, null, null]);
  const [setConflicts, setSetConflicts] = useState([[], [], []]);
  const [generating, setGenerating] = useState([false, false, false]);
  const [combos, setCombos] = useState([[], [], []]);
  const [comboMetas, setComboMetas] = useState([null, null, null]);
  const [savingCombo, setSavingCombo] = useState(null);

  useEffect(() => {
    refreshAllSets();
    axios.get(`${API}/picks`)
      .then(res => { setPicks(res.data.picks || []); setTotalDraws(res.data.total_draws_checked || 0); })
      .catch(() => toast.error('Failed to load picks'))
      .finally(() => setLoading(false));
  }, []);

  const refreshAllSets = () => {
    const newSets = [1, 2, 3].map(n => getAllConstraints(n));
    setSets(newSets);
    setSetConflicts(newSets.map(c => c ? detectConflicts(c) : []));
  };

  const generateForSet = async (setIdx) => {
    const c = sets[setIdx];
    if (!c) return;
    const payload = getAppliedPayload(c);
    if (Object.keys(payload).length === 0) { toast.error(`C${setIdx + 1}: No constraints applied!`); return; }
    setGenerating(prev => { const n = [...prev]; n[setIdx] = true; return n; });
    try {
      const res = await axios.post(`${API}/generate-constrained-combos`, payload);
      setCombos(prev => { const n = [...prev]; n[setIdx] = res.data.combinations || []; return n; });
      setComboMetas(prev => { const n = [...prev]; n[setIdx] = res.data; return n; });
      if (res.data.combinations?.length === 0) {
        toast.warning(`C${setIdx + 1}: No combinations found. Relax constraints.`);
      } else {
        toast.success(`C${setIdx + 1}: Generated ${res.data.combinations.length} combinations`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || `C${setIdx + 1}: Generation failed`); }
    finally { setGenerating(prev => { const n = [...prev]; n[setIdx] = false; return n; }); }
  };

  const generateAll = async () => {
    const hasAny = sets.some((c, i) => c && Object.values(c).some(v => v.applied));
    if (!hasAny) { toast.error('No constraints applied in any set!'); return; }
    for (let i = 0; i < 3; i++) {
      const c = sets[i];
      if (c && Object.values(c).some(v => v.applied)) {
        await generateForSet(i);
      }
    }
  };

  const saveComboAsPick = async (combo, setIdx, comboIdx) => {
    const key = `${setIdx}-${comboIdx}`;
    setSavingCombo(key);
    try {
      await axios.post(`${API}/picks`, { balls: combo.balls, lucky_stars: combo.lucky_stars, notes: `C${setIdx + 1} #${comboIdx + 1} (Score: ${combo.score.toFixed(1)})` });
      toast.success(`C${setIdx + 1} #${comboIdx + 1} saved!`);
      const res = await axios.get(`${API}/picks`);
      setPicks(res.data.picks || []);
      setTotalDraws(res.data.total_draws_checked || 0);
    } catch (e) { toast.error('Failed to save'); }
    finally { setSavingCombo(null); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this pick?')) return;
    try {
      await axios.delete(`${API}/picks/${id}`);
      toast.success('Deleted');
      const res = await axios.get(`${API}/picks`);
      setPicks(res.data.picks || []);
    } catch { toast.error('Failed to delete'); }
  };

  const totalCombos = combos.reduce((sum, arr) => sum + arr.length, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-pink-600 rounded flex items-center justify-center"><Heart size={24} weight="fill" className="text-white" /></div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">W47: My Picks</h1>
                <p className="text-xs text-gray-500">3 constraint sets x 10 = 30 tickets</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={refreshAllSets} className="text-xs" data-testid="refresh-constraints">Refresh Constraints</Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : (
          <>
            <div className="flex gap-2 bg-white border border-gray-200 rounded-lg p-1.5 mb-6" data-testid="mypicks-tabs">
              <button onClick={() => setMainTab('generator')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'generator' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-generator">
                Generate 3x10 Combinations
              </button>
              <button onClick={() => setMainTab('saved')} className={`flex-1 px-4 py-2.5 rounded-md text-sm font-bold transition-colors ${mainTab === 'saved' ? 'bg-[#002FA7] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-testid="tab-saved">
                Saved Picks ({picks.length})
              </button>
            </div>

            {mainTab === 'generator' && (
              <div className="space-y-6">
                {/* Generate All Button */}
                <button onClick={generateAll} disabled={generating.some(Boolean)}
                  className="w-full py-4 rounded-xl font-bold text-base bg-gradient-to-r from-[#002FA7] to-[#0050e0] text-white hover:shadow-xl shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="generate-all-btn">
                  {generating.some(Boolean) ? (
                    <><CircleNotch size={20} className="animate-spin" /> Generating...</>
                  ) : (
                    <><Lightning size={20} weight="fill" /> Generate All 3 Sets (30 Tickets)</>
                  )}
                </button>

                {/* 3 Constraint Set Blocks */}
                {[0, 1, 2].map(setIdx => (
                  <ConstraintSetBlock
                    key={setIdx}
                    setIdx={setIdx}
                    constraints={sets[setIdx]}
                    conflicts={setConflicts[setIdx]}
                    isGenerating={generating[setIdx]}
                    combos={combos[setIdx]}
                    comboMeta={comboMetas[setIdx]}
                    onGenerate={() => generateForSet(setIdx)}
                    savingCombo={savingCombo}
                    onSaveCombo={(combo, comboIdx) => saveComboAsPick(combo, setIdx, comboIdx)}
                    navigate={navigate}
                  />
                ))}

                {/* Total Summary */}
                {totalCombos > 0 && (
                  <div className="bg-gray-800 rounded-xl p-5 text-center" data-testid="total-summary">
                    <div className="text-white/60 text-xs uppercase font-bold mb-1">Total Generated Tickets</div>
                    <div className="text-4xl font-black text-white">{totalCombos}</div>
                    <div className="flex justify-center gap-4 mt-3">
                      {[0, 1, 2].map(i => (
                        <span key={i} className={`text-xs font-bold px-3 py-1 rounded-full ${SET_COLORS[i].bg} text-white`}>
                          C{i + 1}: {combos[i].length} tickets
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {mainTab === 'saved' && (
              <SavedPicksList picks={picks} totalDraws={totalDraws} handleDelete={handleDelete} navigate={navigate} />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function ConstraintSetBlock({ setIdx, constraints, conflicts, isGenerating, combos, comboMeta, onGenerate, savingCombo, onSaveCombo, navigate }) {
  const color = SET_COLORS[setIdx];
  const label = SET_LABELS[setIdx];
  const appliedList = constraints ? Object.values(constraints).filter(v => v.applied) : [];
  const allList = constraints ? Object.entries(constraints) : [];
  const highConflicts = (conflicts || []).filter(c => c.severity === 'high');

  return (
    <div className={`border-2 rounded-2xl overflow-hidden ${color.border}`} data-testid={`constraint-set-${setIdx}`}>
      {/* Set Header */}
      <div className={`bg-gradient-to-r ${color.gradient} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <span className="text-white font-black text-xl">{label}</span>
          <span className="text-white/60 text-xs">Constraint Set {setIdx + 1}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-white/20 text-white">{appliedList.length} applied</span>
          {highConflicts.length > 0 && (
            <span className="text-xs px-2.5 py-1 rounded-full font-bold bg-red-500 text-white">{highConflicts.length} conflicts</span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4 bg-white">
        {/* Constraints Grid */}
        <div data-testid={`constraints-grid-${setIdx}`}>
          <h4 className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1.5">
            <Funnel size={14} /> Constraints
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {allList.map(([key, c]) => (
              <div key={key} className={`border rounded-lg p-2 transition-all ${c.applied ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-100'}`} data-testid={`constraint-${setIdx}-${key}`}>
                <div className="flex items-center gap-1 mb-1">
                  {c.applied ? <CheckCircle size={12} weight="fill" className="text-emerald-500" /> : <XCircle size={12} className="text-gray-300" />}
                  <span className={`text-[10px] font-bold uppercase ${c.applied ? 'text-emerald-700' : 'text-gray-400'}`}>{c.label}</span>
                </div>
                {c.applied ? (
                  <div className="text-[10px] font-mono text-gray-700 truncate" title={c.display}>{c.display}</div>
                ) : (
                  <button onClick={() => navigate(c.path)} className="text-[10px] text-[#002FA7] underline">set</button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Conflicts */}
        {(conflicts || []).length > 0 && (
          <div className={`border rounded-lg p-3 ${highConflicts.length > 0 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`} data-testid={`conflicts-${setIdx}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <Warning size={14} weight="fill" className={highConflicts.length > 0 ? 'text-red-500' : 'text-amber-500'} />
              <span className="text-xs font-bold text-gray-700">{conflicts.length} issue{conflicts.length > 1 ? 's' : ''}</span>
            </div>
            <div className="space-y-1.5">
              {conflicts.map((cf, i) => (
                <div key={i} className="text-[10px] text-gray-600">
                  <span className={`font-black uppercase ${cf.severity === 'high' ? 'text-red-600' : cf.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'}`}>{cf.severity}</span>
                  {' '}{cf.title}: {cf.detail}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Generate Button */}
        <button onClick={onGenerate} disabled={isGenerating || appliedList.length === 0}
          className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
            appliedList.length > 0
              ? highConflicts.length > 0
                ? 'bg-gradient-to-r from-amber-500 to-orange-600 text-white hover:shadow-lg'
                : `bg-gradient-to-r ${color.gradient} text-white hover:shadow-lg`
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`} data-testid={`generate-${setIdx}-btn`}>
          {isGenerating ? (
            <><CircleNotch size={16} className="animate-spin" /> Generating {label}...</>
          ) : appliedList.length === 0 ? (
            `No constraints set for ${label}`
          ) : (
            <><Lightning size={16} weight="fill" /> Generate Top 10 for {label}</>
          )}
        </button>

        {/* Results */}
        {combos.length > 0 && comboMeta && (
          <SetComboResults combos={combos} comboMeta={comboMeta} setIdx={setIdx} savingCombo={savingCombo} onSaveCombo={onSaveCombo} />
        )}
      </div>
    </div>
  );
}

function SetComboResults({ combos, comboMeta, setIdx, savingCombo, onSaveCombo }) {
  const maxScore = combos[0]?.score || 1;
  const color = SET_COLORS[setIdx];
  const label = SET_LABELS[setIdx];
  const fs = comboMeta.filter_stats || {};
  const ld = comboMeta.last_draw || {};

  return (
    <div className="space-y-3">
      {/* Last Draw Reference */}
      {ld.balls && (
        <div className="bg-gray-800 rounded-lg px-4 py-2 flex items-center justify-between" data-testid={`last-draw-${setIdx}`}>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-[10px] font-bold">LAST #{ld.draw_number}:</span>
            <div className="flex gap-1">
              {ld.balls.map((n, i) => <span key={i} className="w-6 h-6 rounded-full bg-gray-600 text-white flex items-center justify-center text-[10px] font-bold">{n}</span>)}
              <span className="text-gray-500 mx-0.5 text-xs">+</span>
              {ld.lucky_stars.map((n, i) => <span key={i} className="w-6 h-6 rounded-full bg-gray-600 text-amber-400 flex items-center justify-center text-[10px] font-bold">{n}</span>)}
            </div>
          </div>
          {comboMeta.next_draw_day && (
            <span className="text-[10px] font-bold text-emerald-400">Next draw: {comboMeta.next_draw_day}</span>
          )}
        </div>
      )}

      {/* Filter Pipeline */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid={`pipeline-${setIdx}`}>
        <div className="text-[10px] font-bold text-gray-400 uppercase mb-2">Filter Pipeline</div>
        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          <span className="bg-gray-200 rounded px-2 py-1 font-bold">{fs.total_unique?.toLocaleString()} total</span>
          {fs.sum_range > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.sum_range?.toLocaleString()} W14</span></>}
          {fs.sign_pattern > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.sign_pattern?.toLocaleString()} W3</span></>}
          {fs.direction > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.direction?.toLocaleString()} W4</span></>}
          {fs.combo_7 > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.combo_7?.toLocaleString()} W5</span></>}
          {fs.range_pattern > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.range_pattern?.toLocaleString()} W24</span></>}
          {fs.shared > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.shared?.toLocaleString()} W17</span></>}
          {fs.repeat > 0 && <><span className="text-gray-300">&rarr;</span><span className="bg-red-100 text-red-600 rounded px-2 py-1 font-bold">-{fs.repeat?.toLocaleString()} Repeat</span></>}
          <span className="text-gray-300">&rarr;</span>
          <span className={`rounded px-2 py-1 font-bold ${comboMeta.total_passed_filters > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{comboMeta.total_passed_filters?.toLocaleString()} passed</span>
        </div>
      </div>

      {/* Combos Table */}
      {combos.length === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center text-xs text-amber-700" data-testid={`no-results-${setIdx}`}>
          <Warning size={20} className="mx-auto mb-1" /> No combinations pass all filters for {label}. Relax constraints.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`combos-${setIdx}`}>
          <div className={`bg-gradient-to-r ${color.gradient} px-4 py-3`}>
            <div className="flex items-center justify-between">
              <span className="text-white font-bold text-sm">{label}: Top {combos.length} Combinations</span>
              <span className="text-white/50 text-[10px]">{comboMeta.total_passed_filters?.toLocaleString()} passed, {comboMeta.total_evaluated?.toLocaleString()} scored</span>
            </div>
            {comboMeta.applied_constraints?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {comboMeta.applied_constraints.map((ac, i) => (
                  <span key={i} className="bg-white/15 text-white/90 text-[10px] px-1.5 py-0.5 rounded font-mono">{ac.window}: {ac.value}</span>
                ))}
              </div>
            )}
          </div>

          <div className="divide-y divide-gray-100">
            {combos.map((combo, idx) => {
              const pct = maxScore > 0 ? (combo.score / maxScore) * 100 : 0;
              const saveKey = `${setIdx}-${idx}`;
              return (
                <div key={idx} className="px-4 py-3 hover:bg-gray-50 transition-colors" data-testid={`combo-${setIdx}-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs shrink-0 ${
                      idx === 0 ? 'bg-yellow-400 text-yellow-900' : idx === 1 ? 'bg-gray-300 text-gray-700' : idx === 2 ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}>{idx + 1}</div>

                    <div className="flex gap-1">
                      {combo.balls.map((num, i) => (
                        <div key={i} className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs bg-[#002FA7] text-white border-2 border-[#001f7a]">{num}</div>
                      ))}
                    </div>
                    <div className="text-gray-300 text-lg font-light">+</div>
                    <div className="flex gap-1">
                      {combo.lucky_stars.map((num, i) => (
                        <div key={i} className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-xs bg-amber-400 text-amber-900 border-2 border-amber-500">{num}</div>
                      ))}
                    </div>

                    <div className="flex-1 ml-2">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${combo.passed >= 10 ? 'bg-emerald-100 text-emerald-700' : combo.passed >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{combo.passed}P/{combo.warnings}W/{combo.failed}F</span>
                        <span className="text-sm font-black text-[#002FA7]">{combo.score.toFixed(1)}</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${combo.score >= 90 ? 'bg-emerald-500' : combo.score >= 70 ? 'bg-[#002FA7]' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>

                    <button onClick={() => onSaveCombo(combo, idx)} disabled={savingCombo === saveKey}
                      className="shrink-0 px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white text-[10px] font-bold hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                      data-testid={`save-combo-${setIdx}-${idx}`}>
                      {savingCombo === saveKey ? <CircleNotch size={12} className="animate-spin" /> : <FloppyDisk size={12} />} Save
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SavedPicksList({ picks, totalDraws, handleDelete, navigate }) {
  const handleCheck = (pick) => {
    navigate(`/prediction-checker?b=${pick.balls.join(',')}&ls=${pick.lucky_stars.join(',')}`);
  };

  if (picks.length === 0) {
    return (
      <div className="bg-white border rounded-xl p-12 text-center">
        <Heart size={48} className="mx-auto text-gray-300 mb-4" />
        <h3 className="text-lg font-bold text-gray-600">No saved picks yet</h3>
        <p className="text-sm text-gray-400 mt-1">Generate combinations and save them to track against draws</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="picks-list">
      <div className="text-xs text-gray-500 mb-2">Tracked against {totalDraws > 0 ? `all ${totalDraws} draws` : 'all draws'} with prize tiers</div>
      {picks.map((pick) => (
        <div key={pick.id} className="bg-white border rounded-xl p-5" data-testid={`pick-${pick.id}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                {pick.balls.map((b, i) => (
                  <span key={i} className="w-10 h-10 rounded-full bg-[#002FA7] text-white flex items-center justify-center font-mono font-bold text-sm">{b}</span>
                ))}
                <span className="text-gray-300 text-lg mx-1">+</span>
                {pick.lucky_stars.map((ls, i) => (
                  <span key={`ls-${i}`} className="w-10 h-10 rounded-full bg-yellow-400 text-yellow-900 flex items-center justify-center font-mono font-bold text-sm">{ls}</span>
                ))}
              </div>
              {pick.notes && <span className="text-xs text-gray-400 italic ml-2">"{pick.notes}"</span>}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleCheck(pick)} className="gap-1 text-xs" data-testid={`check-pick-${pick.id}`}>
                <Target size={14} /> Check
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(pick.id)} className="text-gray-400 hover:text-red-500" data-testid={`delete-pick-${pick.id}`}>
                <Trash size={16} />
              </Button>
            </div>
          </div>
          {pick.best_prize && (
            <div className={`rounded-lg p-3 mb-3 flex items-center justify-between ${
              pick.best_prize.color === 'gold' ? 'bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300' :
              pick.best_prize.color === 'green' ? 'bg-emerald-50 border border-emerald-200' :
              'bg-blue-50 border border-blue-200'
            }`} data-testid={`best-prize-${pick.id}`}>
              <div className="flex items-center gap-3">
                <Trophy size={20} weight="fill" className={pick.best_prize.color === 'gold' ? 'text-yellow-500' : pick.best_prize.color === 'green' ? 'text-emerald-500' : 'text-blue-500'} />
                <div>
                  <span className="font-bold text-sm">{pick.best_prize.name}</span>
                  <span className="text-xs text-gray-500 ml-2">({pick.best_prize.balls}+{pick.best_prize.stars})</span>
                  <span className="text-xs font-bold ml-2">{pick.best_prize.prize}</span>
                </div>
              </div>
              <div className="text-xs text-gray-500">Draw #{pick.best_prize.draw_number} ({pick.best_prize.draw_date})</div>
            </div>
          )}
          {pick.total_prize_wins > 0 && (
            <div className="mb-3"><span className="text-xs font-bold text-gray-500">Prize-winning matches: {pick.total_prize_wins} across all {totalDraws} draws</span></div>
          )}
          {pick.prize_wins && pick.prize_wins.length > 0 && (
            <div className="border-t pt-3 mt-2">
              <h4 className="text-xs font-bold text-gray-500 mb-2 flex items-center gap-1"><Trophy size={12} /> Prize History (top 20)</h4>
              <div className="flex flex-wrap gap-2">
                {pick.prize_wins.map((pw, idx) => (
                  <div key={idx} className="bg-gray-50 border rounded px-3 py-1.5 text-xs flex items-center gap-2" data-testid={`prize-${pick.id}-${idx}`}>
                    <span className="font-mono font-bold text-gray-700">#{pw.draw_number}</span>
                    <span className="text-gray-400">{pw.draw_date}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                      pw.color === 'gold' ? 'bg-yellow-400 text-yellow-900 ring-1 ring-yellow-500' :
                      pw.color === 'green' ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300' :
                      'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                    }`}>{pw.balls}+{pw.stars} {pw.name} {pw.prize}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="text-[10px] text-gray-300 mt-2">Saved: {new Date(pick.created_at).toLocaleDateString()}</div>
        </div>
      ))}
    </div>
  );
}
